/**
 * Staff calls on the sample.
 *
 * The sample has nobody else in it. There is no colleague on the other end of
 * a call, so this stands one in, openly: a call to a sample person rings for
 * two seconds and is then "answered" by a SIMULATED participant. No connection
 * is made to them, their tile shows their initials, and the window says
 * "Sample call — nobody is on this line". Your own microphone and camera are
 * real if the browser lets them be, so the window can be seen working.
 *
 * Everything else is the platform's behaviour on the sample's people: the
 * same decision (domain/calls.ts `decide`) refuses the same calls, a ring
 * nobody answers is missed after the policy's ring time, a call ends when
 * everyone else has left. The policy is kept in this browser
 * (localStorage, read through try/catch) so the "Who can call whom" screen
 * can be worked and its effect seen on the directory.
 *
 * Someone can also call YOU — "Try an incoming call" on the Staff calls
 * screen — so the ring card can be demonstrated without a second device.
 *
 * v2 on the sample: one sample person (Faisal) is on Do not disturb, so a
 * call to him is held and recorded as missed; a meeting is live now and one
 * of yours starts in ten minutes; a huddle you were not invited to can be
 * asked into, and its stand-in host lets you in; in a group call you host
 * with "Ask to join", a stand-in knocks after a few seconds so the lobby can
 * be worked; stand-ins raise a hand and say one line in the chat. All of it
 * is the sample's, and the window says so.
 *
 * DEMONSTRATION records. The people are the sample administration's and a
 * few of the HR sample's trades; none of them is anybody's colleague.
 */
import { DataSourceError } from "../api/dataSource";
import {
  decide,
  decideGroup,
  defaultPolicy,
  dndOutcome,
  isDefaultPolicy,
  joinDecision,
  moderates,
  validateMeeting,
  validatePolicy,
  type Call,
  type CallDirectory,
  type CallMedia,
  type CallMessage,
  type CallPerson,
  type CallPoll,
  type CallPolicy,
  type CallSettingsPatch,
  type CallStartOptions,
  type ControlPayload,
  type Decision,
  type DndState,
  type IceConfig,
  type JoinRule,
  type Level,
  type LiveCall,
  type Meeting,
  type MeetingDraft,
  type Participant,
  type ParticipantRole,
  type Party,
  type PolicyRole,
  type PolicyView,
  type Signal,
} from "../domain/calls";
import { mockDirectory } from "./contacts";

export const SAMPLE_ME = "sample-me";
const ME_NAME = "Operator";
const POLICY_KEY = "sky.calls.policy";
/** how long a sample person takes to pick up */
export const ANSWER_AFTER_MS = 2000;

interface SamplePerson {
  principalId: string;
  name: string;
  level: Level;
  roles: string[];
  jobTitle: string;
  department: string;
  online: boolean;
  device?: "web" | "phone";
  /** minutes since last seen, when offline */
  seenMinsAgo?: number;
  /** a person who is away does not pick up; the ring is missed */
  answers: boolean;
  /** on Do not disturb for this many hours from the sample's start */
  dndHours?: number;
  phones: { label: string; value: string; kind?: string }[];
}

const mob = (n: number) => `+96655${String(3000000 + n).slice(-7)}`;

/* the sample administration's people (api/mockSource.ts) and a few trades from the HR sample */
const PEOPLE: SamplePerson[] = [
  {
    principalId: "sample-2",
    name: "Mona Al-Harbi",
    level: "engineering",
    roles: ["supervisor"],
    jobTitle: "Maintenance supervisor",
    department: "Airfield maintenance",
    online: true,
    device: "web",
    answers: true,
    phones: [{ label: "Mobile", value: mob(12), kind: "mobile" }],
  },
  {
    principalId: "sample-3",
    name: "Khalid Al-Zahrani",
    level: "executive",
    roles: ["accountable_manager"],
    jobTitle: "Accountable manager",
    department: "Management",
    online: true,
    device: "phone",
    answers: true,
    phones: [
      { label: "Mobile", value: mob(13), kind: "mobile" },
      { label: "Office", value: "+966148400013", kind: "phone" },
    ],
  },
  {
    principalId: "sample-4",
    name: "Rashid Al-Dosari",
    level: "operations",
    roles: ["technician"],
    jobTitle: "AGL technician",
    department: "Airfield lighting",
    online: true,
    device: "phone",
    answers: true,
    phones: [{ label: "Mobile", value: mob(14), kind: "mobile" }],
  },
  {
    principalId: "sample-5",
    name: "Sara Al-Amri",
    level: "operations",
    roles: ["viewer"],
    jobTitle: "Airside operations officer",
    department: "Airside operations",
    online: false,
    seenMinsAgo: 25,
    answers: false,
    phones: [{ label: "Mobile", value: mob(15), kind: "mobile" }],
  },
  {
    principalId: "sample-6",
    name: "Faisal Al-Qahtani",
    level: "engineering",
    roles: ["engineer"],
    jobTitle: "Electrical engineer",
    department: "Terminal engineering",
    online: true,
    device: "web",
    answers: true,
    dndHours: 2,
    phones: [{ label: "Mobile", value: mob(16), kind: "mobile" }],
  },
  {
    principalId: "sample-7",
    name: "Noura Al-Otaibi",
    level: "operations",
    roles: ["inspector"],
    jobTitle: "Fire systems technician",
    department: "Fire and life safety",
    online: false,
    seenMinsAgo: 60 * 5,
    answers: false,
    phones: [{ label: "Mobile", value: mob(17), kind: "mobile" }],
  },
  {
    principalId: "sample-8",
    name: "Omar Al-Ghamdi",
    level: "operations",
    roles: ["technician"],
    jobTitle: "HVAC technician",
    department: "Terminal engineering",
    online: true,
    device: "phone",
    answers: true,
    phones: [{ label: "Mobile", value: mob(18), kind: "mobile" }],
  },
];

const ROLES: PolicyRole[] = [
  { key: "viewer", name: "Viewer", level: "operations" },
  { key: "inspector", name: "Inspector", level: "operations" },
  { key: "technician", name: "Technician", level: "operations" },
  { key: "supervisor", name: "Supervisor", level: "engineering" },
  { key: "engineer", name: "Engineer", level: "engineering" },
  { key: "accountable_manager", name: "Accountable manager", level: "executive" },
  { key: "administrator", name: "Administrator", level: "master" },
];

const ME_PARTY: Party = { principalId: SAMPLE_ME, level: "master", roles: ["administrator"] };

function readPolicy(): CallPolicy | null {
  try {
    const raw = localStorage.getItem(POLICY_KEY);
    if (!raw) return null;
    /* a policy kept before Do not disturb existed has no row for it: the default fills it */
    const p = JSON.parse(raw) as Partial<CallPolicy>;
    return { ...defaultPolicy(), ...p, dndOverride: { ...defaultPolicy().dndOverride, ...p.dndOverride } };
  } catch {
    return null;
  }
}

/** 14:00 — the time a Do not disturb ends, as the note about it says. */
function hhmm(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** What the stand-ins say in the chat — one line each, said once. */
const CANNED = [
  "Hello all — can everyone hear me?",
  "I'm on the apron, the signal comes and goes.",
  "Agreed. I'll send the photos after the call.",
];

function writePolicy(p: CallPolicy | null) {
  try {
    if (p) localStorage.setItem(POLICY_KEY, JSON.stringify(p));
    else localStorage.removeItem(POLICY_KEY);
  } catch {
    /* storage unavailable: the policy lasts as long as the page */
  }
}

interface Held {
  call: Call;
  /** when each simulated participant was rung, to pick up two seconds later */
  rungAt: Map<string, number>;
  /** a sample call that carries on without me (the live meeting, the huddle) */
  persistent?: boolean;
  /** the stand-ins' script that has already played on this call */
  played?: Set<string>;
  /** when the stand-in host lets me in from the lobby */
  admitAt?: number;
  /** when I came into the call, for the script */
  meAt?: number;
}

interface MeetingRec {
  m: Meeting;
  invitees: string[];
}

export class MockCalls {
  private calls: Held[] = [];
  private seq = 0;
  private memoryPolicy: CallPolicy | null = null;
  private readonly now: () => number;
  /** my Do not disturb: when it ends ("forever" for until I turn it off), and who rings through */
  private myDnd: { until: number | "forever" | null; allow: string[] } = { until: null, allow: [] };
  /** the sample people's own Do not disturb */
  private readonly theirDnd = new Map<string, number>();
  private messages: CallMessage[] = [];
  private msgSeq = 0;
  private signals: Signal[] = [];
  private sigSeq = 0;
  private meetings: MeetingRec[] = [];
  /** the stand-ins' script runs only where asked — tests that want a quiet call say so */
  private readonly script: boolean;

  constructor(opts: { now?: () => number; script?: boolean } = {}) {
    this.now = opts.now ?? (() => Date.now());
    this.script = opts.script ?? true;
    const now = this.now();
    this.calls = seedHistory(now);
    for (const p of PEOPLE) if (p.dndHours) this.theirDnd.set(p.principalId, now + p.dndHours * 3600_000);
    this.seedMeetings(now);
  }

  /* ── the policy ─────────────────────────────────────────────────────── */

  policy(): CallPolicy {
    return readPolicy() ?? this.memoryPolicy ?? defaultPolicy();
  }

  /* build 5: when the policy was saved, so a save from an older reading is refused as the platform refuses it */
  private policySaved: { at: string; by: string } | null = null;

  policyView(): PolicyView {
    const stored = readPolicy() ?? this.memoryPolicy;
    return {
      policy: stored ?? defaultPolicy(),
      isDefault: !stored,
      levels: ["operations", "engineering", "executive", "master"],
      roles: ROLES,
      updatedAt: this.policySaved?.at,
      updatedBy: this.policySaved?.by,
    };
  }

  savePolicy(p: CallPolicy, basedOn?: string, by = "You"): PolicyView {
    /* the sample knows only the saves made since the page loaded; with none, it has nothing to refuse */
    if (basedOn !== undefined && this.policySaved) {
      const at = this.policySaved.at;
      if (basedOn !== at)
        throw new DataSourceError(
          `${this.policySaved.by} saved the call policy at ${at}, after you opened it. Reload it to see their change, then make yours again.`,
          { status: 409 },
        );
    }
    const problems = validatePolicy(
      p,
      ROLES.map((r) => r.key),
    );
    if (problems.length) throw new DataSourceError(problems.join(" "), { status: 400, detail: problems.join(" ") });
    /* the open default saved is no rule at all, as on the platform: "is default" again */
    if (isDefaultPolicy(p)) this.resetPolicy();
    else {
      this.memoryPolicy = p;
      writePolicy(p);
    }
    this.policySaved = { at: new Date(Math.max(Date.now(), Date.parse(this.policySaved?.at ?? "") + 1 || 0)).toISOString(), by };
    return this.policyView();
  }

  resetPolicy(): void {
    this.memoryPolicy = null;
    writePolicy(null);
  }

  private party(id: string): Party {
    if (id === SAMPLE_ME) return ME_PARTY;
    const p = PEOPLE.find((x) => x.principalId === id);
    if (!p) throw new DataSourceError("Nobody on staff calls has that id.", { status: 404 });
    return { principalId: p.principalId, level: p.level, roles: p.roles };
  }

  check(from: string, to: string): Decision {
    return decide(this.party(from), this.party(to), this.policy());
  }

  /* ── the directory ──────────────────────────────────────────────────── */

  directory(q?: string): CallDirectory {
    const policy = this.policy();
    const now = this.now();
    const needle = (q ?? "").trim().toLowerCase();
    const items: CallPerson[] = PEOPLE.filter(
      (p) => !needle || [p.name, p.jobTitle, p.department].some((v) => v.toLowerCase().includes(needle)),
    ).map((p) => {
      const d = decide(ME_PARTY, this.party(p.principalId), policy);
      return {
        principalId: p.principalId,
        name: p.name,
        level: p.level,
        roles: p.roles,
        jobTitle: p.jobTitle,
        department: p.department,
        hasPhoto: false,
        online: p.online,
        device: p.online ? p.device : undefined,
        lastSeenAt: new Date(now - (p.online ? 0 : (p.seenMinsAgo ?? 0) * 60000)).toISOString(),
        canCall: d.allowed,
        canVideo: d.allowed && d.video,
        reason: d.allowed ? undefined : d.reason,
        phones: p.phones,
        ...(this.onDnd(p.principalId)
          ? { dnd: true, dndUntil: new Date(this.theirDnd.get(p.principalId)!).toISOString() }
          : { dnd: false }),
      };
    });
    /* the contact directory's people with no sign-in: reached by telephone */
    const contacts = mockDirectory().flatMap((o) =>
      o.people
        .filter((p) => !p.principalId)
        .map((p) => ({
          personId: p.id ?? p.name,
          name: p.name,
          org: o.name,
          role: p.role,
          phones: p.points
            .filter((x) => x.kind !== "email")
            .map((x) => ({ label: x.label ?? x.kind, value: x.value, kind: x.kind })),
        }))
        .filter((p) => p.phones.length && (!needle || p.name.toLowerCase().includes(needle))),
    );
    const summary = {
      isDefault: !(readPolicy() ?? this.memoryPolicy),
      myLevel: ME_PARTY.level,
      canVideo: policy.video[ME_PARTY.level],
      canGroup: policy.groupStart[ME_PARTY.level],
      groupMax: policy.groupMax,
      ringSeconds: policy.ringSeconds,
      canOverrideDnd: !!policy.dndOverride?.[ME_PARTY.level],
    };
    return { items, contacts, summary, sample: true };
  }

  /* ── calls ──────────────────────────────────────────────────────────── */

  private person(id: string): Participant {
    const p = PEOPLE.find((x) => x.principalId === id);
    return {
      principalId: id,
      name: p?.name ?? ME_NAME,
      level: p?.level ?? "master",
      state: "ringing",
      audioOn: true,
      videoOn: false,
      hasPhoto: false,
      simulated: id !== SAMPLE_ME,
      role: "member",
      handRaised: false,
      sharing: false,
    };
  }

  private view(h: Held): Call {
    const me = h.call.participants.find((p) => p.principalId === SAMPLE_ME);
    return structuredClone({
      ...h.call,
      me: {
        state: me?.state ?? "left",
        audioOn: me?.audioOn ?? true,
        videoOn: me?.videoOn ?? false,
        principalId: SAMPLE_ME,
        role: me?.role ?? "member",
        handRaised: me?.handRaised ?? false,
        sharing: me?.sharing ?? false,
      },
    });
  }

  private held(id: string): Held {
    const h = this.calls.find((c) => c.call.id === id);
    if (!h) throw new DataSourceError("Not found: no such call", { status: 404 });
    return h;
  }

  /** The time passes: sample people pick up, rings run out, calls with nobody left end. */
  private advance() {
    const now = this.now();
    const policy = this.policy();
    for (const h of this.calls) {
      const c = h.call;
      if (c.state === "ended") continue;
      for (const p of c.participants) {
        if (p.state !== "ringing") continue;
        const rung = h.rungAt.get(p.principalId) ?? Date.parse(c.createdAt);
        const sample = PEOPLE.find((x) => x.principalId === p.principalId);
        if (p.principalId !== SAMPLE_ME && sample?.answers && now - rung >= ANSWER_AFTER_MS) {
          p.state = "joined";
          p.joinedAt = new Date(now).toISOString();
          c.answeredAt ??= p.joinedAt;
          c.state = "active";
        } else if (now - rung >= policy.ringSeconds * 1000) {
          p.state = "missed";
        }
      }
      if (this.script) this.play(h, now);
      this.settle(h);
    }
  }

  /**
   * The stand-ins' part, once each per call while I am on it: a hand goes up,
   * a line is said in the chat, and — in a group call I run that lets people
   * ask — somebody knocks. The huddle's stand-in host lets me in from its lobby.
   */
  private play(h: Held, now: number) {
    const c = h.call;
    const me = c.participants.find((p) => p.principalId === SAMPLE_ME);
    if (me?.state === "waiting" && h.admitAt != null && now >= h.admitAt) {
      me.state = "joined";
      me.joinedAt = new Date(now).toISOString();
      h.meAt = now;
      h.admitAt = undefined;
      this.control(c.id, SAMPLE_ME, { action: "admitted", by: this.nameOf(c.host ?? c.startedBy) });
      return;
    }
    if (me?.state !== "joined" || c.state !== "active") return;
    h.played ??= new Set();
    const since = now - (h.meAt ?? Date.parse(c.answeredAt ?? c.createdAt));
    const stand = c.participants.filter((p) => p.simulated && p.state === "joined");
    if (!stand.length) return;
    const once = (key: string, after: number, fn: () => void) => {
      if (!h.played!.has(key) && since >= after) {
        h.played!.add(key);
        fn();
      }
    };
    if (c.kind === "group") {
      once("hand", 6000, () => {
        const p = stand[stand.length - 1];
        p.handRaised = true;
        p.handAt = new Date(now).toISOString();
      });
    }
    once("chat", 9000, () => {
      const p = stand[0];
      this.pushMessage(c.id, p.principalId, p.name, CANNED[this.msgSeq % CANNED.length]);
    });
    const mode = c.join?.mode ?? "invited";
    if (c.kind === "group" && moderates(c, SAMPLE_ME) && mode !== "invited") {
      once("knock", 8000, () => {
        const on = new Set(c.participants.filter((p) => p.state !== "missed").map((p) => p.principalId));
        const who = PEOPLE.find((p) => p.online && !on.has(p.principalId) && !this.onDnd(p.principalId));
        if (!who) return;
        const row = c.participants.find((p) => p.principalId === who.principalId);
        const state = mode === "open" ? ("joined" as const) : ("waiting" as const);
        if (row) row.state = state;
        else c.participants.push({ ...this.person(who.principalId), state });
        if (state === "joined") {
          const r = c.participants.find((p) => p.principalId === who.principalId)!;
          r.joinedAt = new Date(now).toISOString();
        }
      });
    }
  }

  private nameOf(id: string): string {
    return id === SAMPLE_ME ? ME_NAME : (PEOPLE.find((p) => p.principalId === id)?.name ?? "");
  }

  private control(callId: string, to: string, payload: ControlPayload) {
    if (to !== SAMPLE_ME) return;
    this.signals.push({
      id: ++this.sigSeq,
      callId,
      from: "platform",
      kind: "control",
      payload,
      createdAt: new Date(this.now()).toISOString(),
    });
  }

  private pushMessage(callId: string, from: string, name: string, text: string): CallMessage {
    const m: CallMessage = {
      id: ++this.msgSeq,
      callId,
      from,
      name,
      text,
      createdAt: new Date(this.now()).toISOString(),
    };
    this.messages.push(m);
    return m;
  }

  /** Is this sample person on Do not disturb now. */
  private onDnd(id: string): boolean {
    const until = this.theirDnd.get(id);
    return until != null && until > this.now();
  }

  private settle(h: Held, reason?: Call["endReason"]) {
    const c = h.call;
    if (c.state === "ended") return;
    const joined = c.participants.filter((p) => p.state === "joined").length;
    const ringing = c.participants.filter((p) => p.state === "ringing").length;
    const meJoined = c.participants.some((p) => p.principalId === SAMPLE_ME && p.state === "joined");
    /* with nobody real on the other end, the call ends when I am no longer on it */
    const meRinging = c.participants.some(
      (p) => p.principalId === SAMPLE_ME && (p.state === "ringing" || p.state === "waiting"),
    );
    /* a meeting's call stays open with only its organiser in it, as the platform keeps it, and so
       does any call somebody is waiting to be let into */
    const waiting = c.participants.some((p) => p.state === "waiting");
    const hold = (c.meetingId ? joined >= 1 : joined >= 2) || (joined >= 1 && waiting);
    if ((!hold && ringing === 0) || (!meJoined && !meRinging && !h.persistent)) {
      c.state = "ended";
      c.endedAt = new Date(this.now()).toISOString();
      c.endReason =
        reason ??
        (c.answeredAt
          ? "completed"
          : ringing || c.participants.some((p) => p.state === "missed")
            ? "missed"
            : "cancelled");
      for (const p of c.participants) {
        if (p.state === "ringing") p.state = "missed";
        if (p.state === "joined" || p.state === "waiting") {
          p.state = "left";
        }
        p.sharing = false;
        p.handRaised = false;
      }
      const meeting = c.meetingId ? this.meetings.find((r) => r.m.id === c.meetingId) : undefined;
      if (meeting && meeting.m.state === "live") meeting.m.state = "ended";
    }
  }

  poll(afterMsg = 0): CallPoll {
    this.advance();
    const mine = (state: Participant["state"]) =>
      this.calls
        .filter((h) => h.call.state !== "ended")
        .filter((h) => h.call.participants.some((p) => p.principalId === SAMPLE_ME && p.state === state))
        .map((h) => this.view(h));
    const calls = mine("joined");
    const onIds = new Set(calls.map((c) => c.id));
    const messages = this.messages.filter((m) => m.id > afterMsg && onIds.has(m.callId));
    /* a control signal is delivered once, as the platform's are read once past `after` */
    const signals = this.signals.splice(0);
    const now = this.now();
    return {
      now: new Date(now).toISOString(),
      incoming: mine("ringing"),
      calls,
      waiting: mine("waiting"),
      signals,
      lastSignal: signals.reduce((m, s) => Math.max(m, s.id), 0),
      me: SAMPLE_ME,
      dnd: this.dndState(),
      messages,
      lastMessage: this.msgSeq,
      meetings: this.mine()
        .filter((r) => {
          const start = Date.parse(r.m.startsAt);
          return (
            r.m.state === "live" ||
            (r.m.state === "scheduled" && start - now <= 15 * 60000 && now - start <= r.m.durationMin * 60000)
          );
        })
        .map((r) => this.meetingView(r)),
    };
  }

  create(invitees: string[], media: CallMedia, opts: CallStartOptions = {}): { call: Call; notes: string[] } {
    const policy = this.policy();
    if (!invitees.length) throw new DataSourceError("Choose somebody to call.", { status: 400 });
    if (opts.urgent && !policy.dndOverride[ME_PARTY.level]) {
      const detail = "Your level cannot break through Do not disturb";
      throw new DataSourceError(`Not permitted: ${detail}`, { status: 403, detail });
    }
    const g = decideGroup(
      ME_PARTY,
      invitees.map((i) => this.party(i)),
      invitees.length + 1,
      policy,
    );
    if (!g.allowed) {
      const who = g.who ? PEOPLE.find((p) => p.principalId === g.who)?.name : undefined;
      const detail = who ? `${who}: ${g.reason}` : (g.reason ?? "Refused.");
      throw new DataSourceError(`Not permitted: ${detail}`, { status: 403, detail });
    }
    const notes: string[] = [];
    let m = media;
    if (media === "video" && !policy.video[ME_PARTY.level]) {
      m = "audio";
      notes.push("Your level cannot use video — the call was made as a voice call.");
    }
    const now = this.now();
    const id = `sample-call-${++this.seq}-${now}`;
    const me = {
      ...this.person(SAMPLE_ME),
      state: "joined" as const,
      videoOn: m === "video",
      joinedAt: new Date(now).toISOString(),
    };
    const group = invitees.length > 1;
    const call: Call = {
      id,
      kind: group ? "group" : "direct",
      media: m,
      title: opts.title?.trim() || undefined,
      state: "ringing",
      startedBy: SAMPLE_ME,
      startedByName: ME_NAME,
      createdAt: new Date(now).toISOString(),
      me: { state: "joined", audioOn: true, videoOn: m === "video" },
      participants: [
        { ...me, role: "host" },
        ...invitees.map((i) => ({
          ...this.person(i),
          invitedBy: SAMPLE_ME,
          state: this.ringOrHold(i, !!opts.urgent, notes),
        })),
      ],
      host: SAMPLE_ME,
      join: group && opts.join ? structuredClone(opts.join) : { mode: "invited", allow: [] },
      locked: false,
      urgent: !!opts.urgent,
      sample: true,
    };
    const h: Held = { call, rungAt: new Map(invitees.map((i) => [i, now])), meAt: now };
    this.calls.push(h);
    /* everybody held by Do not disturb: the call ends at once, and the notes say why */
    if (!call.participants.some((p) => p.state === "ringing")) this.settle(h, "missed");
    return { call: this.view(h), notes };
  }

  /** Ring somebody, or hold the ring because they are on Do not disturb — and say so. */
  private ringOrHold(id: string, urgent: boolean, notes: string[]): Participant["state"] {
    const until = this.theirDnd.get(id);
    const out = dndOutcome(
      { dnd: until != null ? { until: new Date(until).toISOString(), allow: [] } : undefined },
      SAMPLE_ME,
      urgent,
      true,
      this.now(),
    );
    if (out !== "held") return "ringing";
    notes.push(`${this.nameOf(id)} is on Do not disturb until ${hhmm(until!)} — they will see a missed call.`);
    return "missed";
  }

  /** A sample person rings me — the ring card, demonstrated. */
  simulateIncoming(media: CallMedia, from = "sample-2"): Call {
    const now = this.now();
    const held = this.myDndOn() && !this.myDnd.allow.includes(from);
    const id = `sample-call-${++this.seq}-${now}`;
    const caller = {
      ...this.person(from),
      state: "joined" as const,
      videoOn: false,
      joinedAt: new Date(now).toISOString(),
    };
    const call: Call = {
      id,
      kind: "direct",
      media,
      state: "ringing",
      startedBy: from,
      startedByName: caller.name,
      createdAt: new Date(now).toISOString(),
      me: { state: "ringing", audioOn: true, videoOn: false },
      participants: [
        { ...caller, role: "host" },
        { ...this.person(SAMPLE_ME), simulated: false, invitedBy: from, state: held ? "missed" : "ringing" },
      ],
      host: from,
      sample: true,
    };
    const h: Held = { call, rungAt: new Map([[SAMPLE_ME, now]]) };
    this.calls.push(h);
    /* on Do not disturb nothing rings: the call is a missed one in the list */
    if (held) this.settle(h, "missed");
    return this.view(h);
  }

  answer(id: string, video: boolean): Call {
    this.advance();
    const h = this.held(id);
    const me = h.call.participants.find((p) => p.principalId === SAMPLE_ME);
    if (h.call.state === "ended" || me?.state !== "ringing")
      throw new DataSourceError("This call is no longer ringing.", { status: 409 });
    me.state = "joined";
    me.joinedAt = new Date(this.now()).toISOString();
    h.meAt = this.now();
    me.videoOn = video && this.policy().video[ME_PARTY.level];
    h.call.state = "active";
    h.call.answeredAt ??= me.joinedAt;
    return this.view(h);
  }

  decline(id: string): Call {
    const h = this.held(id);
    const me = h.call.participants.find((p) => p.principalId === SAMPLE_ME);
    if (me?.state === "ringing") me.state = "declined";
    this.settle(h, "declined");
    return this.view(h);
  }

  leave(id: string): Call {
    const h = this.held(id);
    const me = h.call.participants.find((p) => p.principalId === SAMPLE_ME);
    if (me && (me.state === "joined" || me.state === "ringing" || me.state === "waiting")) {
      me.state = "left";
      me.sharing = false;
      me.handRaised = false;
    }
    h.admitAt = undefined;
    /* the host left a call that carries on: the longest-joined co-host, else member, takes it */
    if (me?.role === "host" && h.call.state !== "ended") {
      const next = h.call.participants
        .filter((p) => p.state === "joined" && p.principalId !== SAMPLE_ME)
        .sort(
          (a, b) =>
            Number(b.role === "cohost") - Number(a.role === "cohost") ||
            (a.joinedAt ?? "").localeCompare(b.joinedAt ?? ""),
        )[0];
      if (next) {
        me.role = "member";
        next.role = "host";
        h.call.host = next.principalId;
      }
    }
    this.settle(h);
    return this.view(h);
  }

  invite(id: string, invitees: string[]): Call {
    this.advance();
    const h = this.held(id);
    if (h.call.state === "ended") throw new DataSourceError("This call has ended.", { status: 409 });
    const on = h.call.participants.filter((p) => p.state === "joined" || p.state === "ringing");
    const fresh = invitees.filter((i) => !on.some((p) => p.principalId === i));
    const g = decideGroup(
      ME_PARTY,
      fresh.map((i) => this.party(i)),
      on.length + fresh.length,
      this.policy(),
    );
    if (!g.allowed) {
      const who = g.who ? PEOPLE.find((p) => p.principalId === g.who)?.name : undefined;
      const detail = who ? `${who}: ${g.reason}` : (g.reason ?? "Refused.");
      throw new DataSourceError(`Not permitted: ${detail}`, { status: 403, detail });
    }
    const now = this.now();
    const notes: string[] = [];
    for (const i of fresh) {
      const existing = h.call.participants.find((p) => p.principalId === i);
      const state = this.ringOrHold(i, false, notes);
      if (existing) existing.state = state;
      else h.call.participants.push({ ...this.person(i), invitedBy: SAMPLE_ME, state });
      h.rungAt.set(i, now);
    }
    h.call.kind = "group";
    return this.view(h);
  }

  media(id: string, audioOn: boolean, videoOn: boolean, sharing?: boolean): Call {
    const h = this.held(id);
    const me = h.call.participants.find((p) => p.principalId === SAMPLE_ME);
    if (me) {
      me.audioOn = audioOn;
      me.videoOn = videoOn && this.policy().video[ME_PARTY.level];
      if (sharing !== undefined) {
        /* one screen at a time: mine stops anybody else's */
        if (sharing) for (const p of h.call.participants) if (p !== me) p.sharing = false;
        me.sharing = sharing;
      }
    }
    return this.view(h);
  }

  /* ── v2: my Do not disturb ──────────────────────────────────────────── */

  private myDndOn(): boolean {
    const u = this.myDnd.until;
    return u === "forever" || (u != null && u > this.now());
  }

  dndState(): DndState {
    const on = this.myDndOn();
    return {
      on,
      until: on && typeof this.myDnd.until === "number" ? new Date(this.myDnd.until).toISOString() : undefined,
      forever: on && this.myDnd.until === "forever",
      allow: this.myDnd.allow.map((id) => ({ principalId: id, name: this.nameOf(id) })),
    };
  }

  setDnd(until: string | "forever" | null, allow: string[]): DndState {
    if (until && until !== "forever") {
      const t = Date.parse(until);
      if (!Number.isFinite(t) || t <= this.now())
        throw new DataSourceError("That time has passed.", { status: 400, detail: "That time has passed." });
      this.myDnd = { until: t, allow: [...new Set(allow)] };
    } else {
      this.myDnd = { until: until === "forever" ? "forever" : null, allow: [...new Set(allow)] };
    }
    return this.dndState();
  }

  /* ── v2: coming into a call, and running one ────────────────────────── */

  private moderated(id: string): Held {
    this.advance();
    const h = this.held(id);
    if (h.call.state === "ended") throw new DataSourceError("This call has ended.", { status: 409 });
    if (!moderates(h.call, SAMPLE_ME)) {
      const detail = "Only the host or a co-host can do that.";
      throw new DataSourceError(`Not permitted: ${detail}`, { status: 403, detail });
    }
    return h;
  }

  private row(h: Held, id: string): Participant {
    const p = h.call.participants.find((x) => x.principalId === id);
    if (!p) throw new DataSourceError("That person is not on this call.", { status: 404 });
    return p;
  }

  join(id: string): { call: Call; state: "joined" | "waiting" } {
    this.advance();
    const h = this.held(id);
    const host = h.call.host ?? h.call.startedBy;
    const d = decide(ME_PARTY, this.party(host), this.policy());
    const out = joinDecision(h.call, ME_PARTY, d);
    if (out.outcome === "refused") {
      const status = out.reason === "This call has ended." ? 409 : 403;
      throw new DataSourceError(out.reason, { status, detail: out.reason });
    }
    const now = this.now();
    let me = h.call.participants.find((p) => p.principalId === SAMPLE_ME);
    if (!me) {
      me = { ...this.person(SAMPLE_ME), simulated: false };
      h.call.participants.push(me);
    }
    if (out.outcome === "waiting") {
      me.state = "waiting";
      /* the stand-in host looks at the lobby and lets me in */
      h.admitAt = now + 3000;
      return { call: this.view(h), state: "waiting" };
    }
    if (me.state !== "joined") {
      me.state = "joined";
      me.joinedAt = new Date(now).toISOString();
      h.meAt = now;
    }
    h.call.state = "active";
    h.call.answeredAt ??= me.joinedAt;
    return { call: this.view(h), state: "joined" };
  }

  settings(id: string, patch: CallSettingsPatch): Call {
    const h = this.moderated(id);
    if (patch.join) h.call.join = structuredClone(patch.join);
    if (patch.locked !== undefined) h.call.locked = patch.locked;
    if (patch.title !== undefined) h.call.title = patch.title.trim() || undefined;
    h.call.kind = "group";
    return this.view(h);
  }

  admit(id: string, who: string, allow: boolean): Call {
    const h = this.moderated(id);
    const p = this.row(h, who);
    if (p.state !== "waiting") throw new DataSourceError("That person is not waiting.", { status: 409 });
    if (allow) {
      const on = h.call.participants.filter((x) => x.state === "joined" || x.state === "ringing").length;
      if (on + 1 > this.policy().groupMax) {
        const detail = `A call holds at most ${this.policy().groupMax} people.`;
        throw new DataSourceError(detail, { status: 409, detail });
      }
      p.state = "joined";
      p.joinedAt = new Date(this.now()).toISOString();
    } else p.state = "declined";
    return this.view(h);
  }

  remove(id: string, who: string): Call {
    const h = this.moderated(id);
    const p = this.row(h, who);
    if (p.role === "host") throw new DataSourceError("The host cannot be removed.", { status: 409 });
    p.state = "removed";
    p.sharing = false;
    p.handRaised = false;
    this.settle(h);
    return this.view(h);
  }

  mute(id: string, who: string | "all"): Call {
    const h = this.moderated(id);
    for (const p of h.call.participants) {
      if (p.principalId === SAMPLE_ME || p.state !== "joined") continue;
      if (who === "all" || p.principalId === who) p.audioOn = false;
    }
    return this.view(h);
  }

  role(id: string, who: string, role: ParticipantRole): Call {
    this.advance();
    const h = this.held(id);
    if (roleOfMe(h.call) !== "host") {
      const detail = "Only the host can change roles.";
      throw new DataSourceError(`Not permitted: ${detail}`, { status: 403, detail });
    }
    const p = this.row(h, who);
    if (role === "host") {
      const me = this.row(h, SAMPLE_ME);
      me.role = "cohost";
      p.role = "host";
      h.call.host = who;
    } else p.role = role;
    return this.view(h);
  }

  end(id: string): Call {
    const h = this.moderated(id);
    for (const p of h.call.participants) if (p.state === "joined" || p.state === "waiting") p.state = "left";
    h.persistent = false;
    this.settle(h, "completed");
    return this.view(h);
  }

  hand(id: string, up: boolean): Call {
    const h = this.held(id);
    const me = this.row(h, SAMPLE_ME);
    me.handRaised = up;
    me.handAt = up ? new Date(this.now()).toISOString() : undefined;
    return this.view(h);
  }

  messagesFor(id: string, after = 0): CallMessage[] {
    this.held(id);
    return this.messages.filter((m) => m.callId === id && m.id > after);
  }

  sendMessage(id: string, text: string): CallMessage {
    const h = this.held(id);
    const me = h.call.participants.find((p) => p.principalId === SAMPLE_ME);
    if (me?.state !== "joined") throw new DataSourceError("You are not on this call.", { status: 403 });
    const clean = text.trim().slice(0, 2000);
    if (!clean) throw new DataSourceError("Write something first.", { status: 400 });
    return this.pushMessage(id, SAMPLE_ME, ME_NAME, clean);
  }

  /** Group calls going on that I could come into, straight in or by asking. */
  live(): LiveCall[] {
    this.advance();
    const out: LiveCall[] = [];
    for (const h of this.calls) {
      const c = h.call;
      if (c.state === "ended" || c.kind !== "group") continue;
      const me = c.participants.find((p) => p.principalId === SAMPLE_ME);
      if (me && (me.state === "joined" || me.state === "removed")) continue;
      const host = c.host ?? c.startedBy;
      const d = decide(ME_PARTY, this.party(host), this.policy());
      const out1 = joinDecision(c, ME_PARTY, d);
      if (out1.outcome === "refused") continue;
      out.push({ ...this.view(h), can: out1.outcome === "waiting" ? "ask" : "join" });
    }
    return out;
  }

  /* ── v2: meetings ───────────────────────────────────────────────────── */

  private mine(): MeetingRec[] {
    return this.meetings.filter((r) => r.m.organiser === SAMPLE_ME || r.invitees.includes(SAMPLE_ME));
  }

  private meetingView(r: MeetingRec): Meeting {
    const now = this.now();
    const start = Date.parse(r.m.startsAt);
    const organiser = r.m.organiser === SAMPLE_ME;
    const open = r.m.state === "scheduled" || r.m.state === "live";
    return structuredClone({
      ...r.m,
      endsAt: new Date(start + r.m.durationMin * 60000).toISOString(),
      canEdit: organiser && r.m.state === "scheduled",
      canStart: open && (organiser || r.m.state === "live" || start - now <= 10 * 60000),
    });
  }

  meetingList(scope: "upcoming" | "past"): Meeting[] {
    this.advance();
    const rows = this.mine().filter((r) =>
      scope === "upcoming"
        ? r.m.state === "scheduled" || r.m.state === "live"
        : r.m.state === "ended" || r.m.state === "cancelled",
    );
    rows.sort((a, b) =>
      scope === "upcoming" ? a.m.startsAt.localeCompare(b.m.startsAt) : b.m.startsAt.localeCompare(a.m.startsAt),
    );
    return rows.slice(0, 50).map((r) => this.meetingView(r));
  }

  saveMeeting(d: MeetingDraft, id?: string): Meeting {
    const policy = this.policy();
    const problems = validateMeeting(d, policy.groupMax, this.now());
    if (problems.length) throw new DataSourceError(problems.join(" "), { status: 400, detail: problems.join(" ") });
    const g = decideGroup(
      ME_PARTY,
      d.invitees.map((i) => this.party(i)),
      d.invitees.length + 1,
      policy,
    );
    if (!g.allowed) {
      const who = g.who ? this.nameOf(g.who) : undefined;
      const detail = who ? `${who}: ${g.reason}` : (g.reason ?? "Refused.");
      throw new DataSourceError(`Not permitted: ${detail}`, { status: 403, detail });
    }
    const invitees = d.invitees.map((i) => this.invitee(i));
    if (id) {
      const r = this.meetings.find((x) => x.m.id === id);
      if (!r) throw new DataSourceError("Not found: no such meeting", { status: 404 });
      if (r.m.organiser !== SAMPLE_ME || r.m.state !== "scheduled")
        throw new DataSourceError("Only the organiser can change a meeting before it starts.", { status: 403 });
      Object.assign(r.m, {
        title: d.title.trim(),
        agenda: d.agenda.trim() || undefined,
        startsAt: new Date(Date.parse(d.startsAt)).toISOString(),
        durationMin: d.durationMin,
        invitees,
        join: structuredClone(d.join),
        media: d.media,
      });
      r.invitees = [...d.invitees];
      return this.meetingView(r);
    }
    const now = this.now();
    const r: MeetingRec = {
      invitees: [...d.invitees],
      m: {
        id: `sample-meeting-${++this.seq}-${now}`,
        title: d.title.trim(),
        agenda: d.agenda.trim() || undefined,
        startsAt: new Date(Date.parse(d.startsAt)).toISOString(),
        endsAt: "",
        durationMin: d.durationMin,
        organiser: SAMPLE_ME,
        organiserName: ME_NAME,
        invitees,
        join: structuredClone(d.join),
        media: d.media,
        state: "scheduled",
        canStart: true,
        canEdit: true,
        sample: true,
      },
    };
    this.meetings.push(r);
    return this.meetingView(r);
  }

  cancelMeeting(id: string): Meeting {
    const r = this.meetings.find((x) => x.m.id === id);
    if (!r) throw new DataSourceError("Not found: no such meeting", { status: 404 });
    if (r.m.organiser !== SAMPLE_ME)
      throw new DataSourceError("Only the organiser can cancel a meeting.", { status: 403 });
    if (r.m.state !== "scheduled")
      throw new DataSourceError("Only a meeting not yet started can be cancelled.", { status: 409 });
    r.m.state = "cancelled";
    return this.meetingView(r);
  }

  startMeeting(id: string): { meeting: Meeting; call: Call; notes?: string[] } {
    this.advance();
    const r = this.meetings.find((x) => x.m.id === id);
    if (!r) throw new DataSourceError("Not found: no such meeting", { status: 404 });
    const view = this.meetingView(r);
    if (!view.canStart) {
      const detail = "The meeting opens 10 minutes before it starts.";
      throw new DataSourceError(`Not permitted: ${detail}`, { status: 403, detail });
    }
    if (r.m.state === "live" && r.m.callId) {
      const j = this.join(r.m.callId);
      return { meeting: this.meetingView(r), call: j.call };
    }
    const others = [r.m.organiser, ...r.invitees].filter((x) => x !== SAMPLE_ME);
    const created = this.create(others, r.m.media, { title: r.m.title, join: r.m.join });
    const h = this.held(created.call.id);
    h.call.kind = "group";
    h.call.meetingId = r.m.id;
    if (h.call.state !== "ended") {
      r.m.state = "live";
      r.m.callId = h.call.id;
    }
    return { meeting: this.meetingView(r), call: this.view(h), notes: created.notes };
  }

  private invitee(id: string) {
    const p = PEOPLE.find((x) => x.principalId === id);
    return { principalId: id, name: p?.name ?? ME_NAME, level: p?.level ?? ("master" as Level), hasPhoto: false };
  }

  /**
   * The sample's meetings: one live now (Khalid's morning briefing, open to
   * the invited), one of mine in ten minutes, one of Mona's tomorrow, and two
   * past. And a huddle going on that I was not invited to, which I can ask into.
   */
  private seedMeetings(now: number) {
    const at = (mins: number) => new Date(now + mins * 60000).toISOString();
    const rec = (
      id: string,
      title: string,
      agenda: string,
      startMins: number,
      duration: number,
      organiser: string,
      invitees: string[],
      join: JoinRule,
      state: Meeting["state"],
    ): MeetingRec => ({
      invitees,
      m: {
        id,
        title,
        agenda,
        startsAt: at(startMins),
        endsAt: "",
        durationMin: duration,
        organiser,
        organiserName: this.nameOf(organiser),
        invitees: invitees.map((i) => this.invitee(i)),
        join,
        media: "video",
        state,
        canStart: false,
        canEdit: false,
        sample: true,
      },
    });
    const briefing = rec(
      "sample-meeting-live",
      "Morning operations briefing",
      "Overnight faults, today's stand plan, the apron works on taxiway B.",
      -6,
      30,
      "sample-3",
      [SAMPLE_ME, "sample-2", "sample-4"],
      { mode: "open", allow: [] },
      "live",
    );
    this.meetings = [
      briefing,
      rec(
        "sample-meeting-soon",
        "Apron safety walk — briefing",
        "Route, PPE, the two open FOD reports, who signs off.",
        10,
        20,
        SAMPLE_ME,
        ["sample-2", "sample-4", "sample-8"],
        { mode: "approval", allow: [{ type: "level", id: "executive" }] },
        "scheduled",
      ),
      rec(
        "sample-meeting-tomorrow",
        "Weekly maintenance review",
        "Open work orders over 7 days; parts on hold; next week's planned jobs.",
        minutesToTomorrowAt(now, 9),
        45,
        "sample-2",
        [SAMPLE_ME, "sample-6", "sample-8"],
        { mode: "invited", allow: [] },
        "scheduled",
      ),
      rec(
        "sample-meeting-past",
        "Fire drill debrief",
        "Response times, the alarm panel fault, actions.",
        -60 * 22,
        30,
        SAMPLE_ME,
        ["sample-7", "sample-3"],
        { mode: "invited", allow: [] },
        "ended",
      ),
      rec(
        "sample-meeting-cancelled",
        "Contractor induction",
        "Moved to next week.",
        -60 * 3,
        30,
        "sample-3",
        [SAMPLE_ME, "sample-4"],
        { mode: "invited", allow: [] },
        "cancelled",
      ),
    ];
    /* the briefing's call: Khalid hosts, Mona is on; I was rung and missed it */
    const call = (
      id: string,
      title: string,
      host: string,
      on: string[],
      me: Participant["state"] | null,
      join: JoinRule,
      startedMins: number,
      meetingId?: string,
    ): Held => {
      const started = new Date(now - startedMins * 60000).toISOString();
      const parts: Participant[] = [host, ...on].map((pid, i) => ({
        ...this.person(pid),
        state: "joined",
        joinedAt: started,
        role: i === 0 ? "host" : "member",
        videoOn: true,
      }));
      if (me) parts.push({ ...this.person(SAMPLE_ME), simulated: false, state: me, invitedBy: host });
      return {
        persistent: true,
        rungAt: new Map(),
        call: {
          id,
          kind: "group",
          media: "video",
          title,
          state: "active",
          startedBy: host,
          startedByName: this.nameOf(host),
          createdAt: started,
          answeredAt: started,
          me: { state: me ?? "left", audioOn: true, videoOn: false },
          participants: parts,
          host,
          join,
          locked: false,
          urgent: false,
          meetingId,
          sample: true,
        },
      };
    };
    const live = call(
      "sample-call-briefing",
      briefing.m.title,
      "sample-3",
      ["sample-2"],
      "missed",
      briefing.m.join,
      6,
      briefing.m.id,
    );
    briefing.m.callId = live.call.id;
    const huddle = call(
      "sample-call-huddle",
      "T2 chiller fault huddle",
      "sample-8",
      ["sample-4"],
      null,
      { mode: "approval", allow: [] },
      4,
    );
    this.calls.push(live, huddle);
  }

  history(limit = 50): Call[] {
    this.advance();
    return this.calls
      .filter((h) => h.call.state === "ended")
      .sort((a, b) => b.call.createdAt.localeCompare(a.call.createdAt))
      .slice(0, limit)
      .map((h) => this.view(h));
  }

  ice(): IceConfig {
    return { iceServers: [], relay: false };
  }
}

function roleOfMe(c: Call): ParticipantRole {
  return (
    c.participants.find((p) => p.principalId === SAMPLE_ME)?.role ?? (c.startedBy === SAMPLE_ME ? "host" : "member")
  );
}

/** Minutes from now until a given hour tomorrow, by this device's clock. */
function minutesToTomorrowAt(now: number, hour: number): number {
  const d = new Date(now);
  d.setDate(d.getDate() + 1);
  d.setHours(hour, 0, 0, 0);
  /* not rounded: a meeting at nine is at nine, whatever second the sample began */
  return (d.getTime() - now) / 60000;
}

/** A few past calls, so Recent calls has something to show — one of them missed. */
function seedHistory(now: number): Held[] {
  const at = (minsAgo: number) => new Date(now - minsAgo * 60000).toISOString();
  const person = (id: string, state: Participant["state"]): Participant => {
    const p = PEOPLE.find((x) => x.principalId === id);
    return {
      principalId: id,
      name: p?.name ?? ME_NAME,
      level: p?.level ?? "master",
      state,
      audioOn: true,
      videoOn: false,
      hasPhoto: false,
      simulated: id !== SAMPLE_ME,
    };
  };
  const past = (
    id: string,
    startedBy: string,
    other: string,
    media: CallMedia,
    minsAgo: number,
    talkMins: number | null,
    myState: Participant["state"],
  ): Held => ({
    rungAt: new Map(),
    call: {
      id,
      kind: "direct",
      media,
      state: "ended",
      startedBy,
      startedByName: startedBy === SAMPLE_ME ? ME_NAME : PEOPLE.find((p) => p.principalId === startedBy)?.name,
      createdAt: at(minsAgo),
      answeredAt: talkMins != null ? at(minsAgo - 0.2) : undefined,
      endedAt: at(minsAgo - (talkMins ?? 0.75)),
      endReason: talkMins != null ? "completed" : "missed",
      me: { state: myState, audioOn: true, videoOn: false },
      participants: [
        person(SAMPLE_ME, myState),
        person(other, talkMins != null ? "left" : startedBy === other ? "left" : "missed"),
      ],
      sample: true,
    },
  });
  return [
    past("sample-history-1", "sample-4", "sample-4", "audio", 38, null, "missed"),
    past("sample-history-2", SAMPLE_ME, "sample-2", "video", 95, 6, "left"),
    past("sample-history-3", "sample-3", "sample-3", "audio", 60 * 26, 3, "left"),
  ];
}
